'use client';

import type { AnalyticsSnippet } from '@segment/analytics-next';
import { assertAllConsent, setupFidesListeners } from './consentHelpers';
import type { SegmentLoadOptions } from './segmentConsent';
import {
  FIDES_CONSENT_EVENTS,
  hasSegmentLoadConsent,
  toSegmentConsentContext,
  toSegmentLoadOptions,
  VENDOR_TIMEOUT_MS,
} from './segmentConsent';
import type { EventType, Identity, Group, Load, Page } from './types';

declare global {
  interface Window {
    analytics?: AnalyticsSnippet;
    gtag: (event: string, action: string, options: object) => void;
    fidesBucket: string;
    Optanon?: any;
    amplitude: any;
    ttq?: any;
    fbq?: any;
    uetq?: any;
    clarity?: any;
    toAppend?: any[];
    DD_RUM?: any;
    optimizely?: any;
    Fides?: {
      showModal: () => void;
      initialized: boolean;
      shouldShowExperience: () => boolean;
      identity: {
        email: string;
        fides_user_device_id: string;
      };
      consent: {
        functional: boolean;
        marketing: boolean;
        analytics: boolean;
        strictly_necessary: boolean;
      };
      config: {
        propertyId: string;
      };
      geolocation: {
        location: string;
        region: string;
        country: string;
      };
      experience: {
        experience_config: {
          name: string;
          id: string;
        };
        privacy_notices: [
          {
            translations: [
              {
                language: string;
                title: string;
                description: string;
                privacy_notice_history_id: string;
              },
            ];
          },
        ];
      };
    };
  }
}

// https://segment.com/docs/guides/filtering-data/#filtering-with-the-integrations-object
export const getSegmentIntegrations = () => {
  if (typeof window === 'undefined' || !window.Fides) {
    return {};
  }
  return toSegmentLoadOptions(window.Fides.consent);
};

//https://segment.com/docs/privacy/consent-management/consent-in-segment-connections/#consent-object
// manual consent management in Segment requires adding a context object to the track call
export const maybeAddSegmentContext = (context?: any) => {
  if (typeof window !== 'undefined' && window.Fides) {
    return { ...context, ...toSegmentConsentContext(window.Fides.consent) };
  } else {
    return {};
  }
};

export const getConsentIDs = (properties: any) => {
  if (typeof window !== 'undefined' && window.Fides) {
    return {
      ...properties,
      consentOrigin: 'fides',
      fides_id: window.Fides?.identity?.fides_user_device_id,
      uuid: crypto?.randomUUID?.(),
    };
  } else {
    return properties;
  }
};

export const maybeAddConsentOrigin = (properties: any) => {
  if (typeof window !== 'undefined' && window.Fides) {
    return {
      ...properties,
      consentOrigin: 'fides',
      fides_id: window.Fides?.identity?.fides_user_device_id,
      uuid: crypto?.randomUUID?.(),
    };
  } else {
    return properties;
  }
};

export interface AnalyticsInterface {
  track<T extends object>(event: EventType<T>): void;
  identify<T extends object>(payload: Identity<T>): void;
  group<T extends object>(group: Group<T>): void;
  reset(): void;
  load(load: Load, options?: SegmentLoadOptions): void;
  page<T extends object>(page: Page<T>): void;
  ready(callback: () => void): void;
  anonymousId(callback: (anonymousId: string) => void): void;
}

/**
 * Lazy-init `analytics` and call through, or throw if window.analytics object is not found
 *
 * @param {string} fn The SegmentAnalytics function to call
 * @param {any[]} args Arguments to pass along
 * @returns Result of calling through
 * @throws If Segment is not yet loaded (checks `window.analytics`)
 */
const initAndCallOrThrow = (fn: string, args: any) => {
  const winAnalytics = typeof window === 'undefined' ? undefined : window?.analytics;
  if (winAnalytics) {
    setAnalytics(winAnalytics);
    // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AnalyticsSnippet'.
    return winAnalytics[fn].apply(window, args);
  } else {
    throw Error(
      `Attempted to call ${fn}() in analytics before it is initialized, and no window.analytics exists.`,
    );
  }
};

/**
 * App wide analytics instance.
 */
let instance: AnalyticsInterface | any = {
  track: (...args: any[]) => initAndCallOrThrow('track', args),
  identify: (...args: any[]) => initAndCallOrThrow('identify', args),
  group: (...args: any[]) => initAndCallOrThrow('group', args),
  reset: (...args: any[]) => initAndCallOrThrow('reset', args),
  load: (...args: any[]) => initAndCallOrThrow('load', args),
  page: (...args: any[]) => initAndCallOrThrow('page', args),
  ready: (...args: any[]) => initAndCallOrThrow('ready', args),
  anonymousId: (...args: any[]) => initAndCallOrThrow('anonymousId', args),
};

export const setAnalytics = (analytics: AnalyticsInterface | AnalyticsSnippet) =>
  (instance = analytics);

export const identify = <T extends object>(payload: Identity<T>) => {
  const properties = maybeAddConsentOrigin(payload.properties);
  const context = maybeAddSegmentContext();
  const integrations = getSegmentIntegrations();
  const options = {
    ...integrations,
    ...context,
  };
  const event = { ...payload, properties, ...options };
  instance.identify(event, event, options);
};

export const track = <T extends object>(event: EventType<T>) => {
  const properties = maybeAddConsentOrigin(event.properties);
  const context = maybeAddSegmentContext();
  const integrations = getSegmentIntegrations();
  const options = {
    ...integrations,
    ...context,
  };
  const payload = { ...event, properties, options };
  return instance.track(payload, payload, options);
};

export const group = <T extends object>(event: Group<T>) => {
  instance.group(event);
};

export const reset = () => {
  instance.reset();
};

export const load = (config: Load, analytics?: AnalyticsInterface) => {
  if (analytics) {
    instance = analytics;
  }
  // Fides CRA apps
  setupFidesListeners();

  if (typeof window === 'undefined') {
    return;
  }

  let hasLoaded = false;
  let isFidesAbsent = false;

  const maybeLoad = () => {
    if (hasLoaded || !window.analytics) {
      return;
    }
    if (!isFidesAbsent || window.Fides) {
      if (!window.Fides?.initialized || !hasSegmentLoadConsent(window.Fides?.consent)) {
        return;
      }
    }

    hasLoaded = true;
    FIDES_CONSENT_EVENTS.forEach(event => window.removeEventListener(event, maybeLoad));
    clearInterval(snippetPoll);
    clearTimeout(fidesTimeout);

    instance.load(config, toSegmentLoadOptions(window.Fides?.consent));
    instance.ready(() => {
      assertAllConsent();
    });
  };

  const snippetPoll = setInterval(() => {
    if (!window.analytics) {
      return;
    }
    clearInterval(snippetPoll);
    maybeLoad();
  }, 500);

  const fidesTimeout = setTimeout(() => {
    if (window.Fides?.initialized) {
      return;
    }
    isFidesAbsent = true;
    maybeLoad();
  }, VENDOR_TIMEOUT_MS);

  FIDES_CONSENT_EVENTS.forEach(event => window.addEventListener(event, maybeLoad));

  maybeLoad();
};

export const page = <T extends object>(event: Page<T>) => {
  const properties = maybeAddConsentOrigin(event.properties);
  const context = maybeAddSegmentContext();
  const integrations = getSegmentIntegrations();
  const options = {
    ...integrations,
    ...context,
  };
  const payload = { ...event, properties, options };
  instance.page(payload, undefined, options);
};

export const anonymousId = (callback: (anonymousId: string) => void) => {
  instance.anonymousId(callback);
};

export const ready: AnalyticsInterface['ready'] = cb => {
  instance?.ready(cb);
};
